import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import EditIcon from "@mui/icons-material/Edit";
import {
  Autocomplete,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { FieldArray, Form, Formik, getIn } from "formik";
import * as Yup from "yup";
import { Close } from "@mui/icons-material";
import {
  useUpdateVibeMutation,
} from "../../Slices/roomNateApis";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import { LoadingButton } from "@mui/lab";
import { useGlobalAuthContext } from "../../context/AuthContext";
import axios from "axios";
import { baseUrl } from "../../utils/baseUrl";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  height: "90%",
  overflowY: "auto",
  width: { xs: "90%", md: "650px" },
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

export default function BasicModal({ FetchStyles, item }) {
  const formRef = React.useRef();
  const [updateVibe, resultStats] = useUpdateVibeMutation();
  const { currentUserSessionStorage } = useGlobalAuthContext();

  const [open, setOpen] = React.useState(false);
  const [load, setLoad] = React.useState(false);
  const [successAlert, setSuccessAlert] = React.useState(false);
  const [errorAlert, setErrorAlert] = React.useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const validationSchema = Yup.object().shape({
    midJourneyPrompt: Yup.string().required("Mid Journey is required"),
    description: Yup.string().required("Description is required"),
    name: Yup.string().required("Name is required"),
    style: Yup.mixed().notRequired("Style is not required"),
    furnitureItems: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().notRequired("Name is notRequired"),
        description: Yup.string().notRequired("Description is notRequired"),
        image: Yup.mixed().notRequired("Image is notRequired"),
        url: Yup.mixed().notRequired("Url is notRequired"),
        embedField: Yup.mixed().notRequired("Embed Field is required"),
      })
    ),
    images: Yup.array().of(
      Yup.object().shape({
        url: Yup.string().required("image url is required"),
      })
    ),
    moodboardImages: Yup.array().min(1, "Please upload at least one image"),
  });

  React.useEffect(() => {
    if (resultStats?.isLoading) {
      setLoad(true);
    }

    if (!resultStats?.isLoading) {
      setLoad(false);
    }

    if (resultStats?.isSuccess) {
      setSuccessAlert(true);
      setTimeout(() => {
        setSuccessAlert(false);
      }, 2000);
      handleClose();
    }

    if (resultStats?.isError) {
      setErrorAlert(true);
      setTimeout(() => {
        setErrorAlert(false);
      }, 2000);
    }
  }, [resultStats]);


  return (
    <>
      {successAlert && (
        <SuccessAlert title="Vibe has been updated successfully!" />
      )}

      {errorAlert && <ErrorAlert title="Something Went Wrong!" />}

      <Tooltip title="Edit">
        <IconButton color="primary" onClick={handleOpen}>
          <EditIcon />
        </IconButton>
      </Tooltip>

      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Box position="fixed" top={10} right={0}>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
          <Formik
            innerRef={formRef}
            enableReinitialize={true}
            initialValues={{
              style:
                FetchStyles?.find((item1) => item1._id === item.style)?._id ||
                "",
              midJourneyPrompt: item?.midJourneyPrompt,
              name: item?.name,
              description: item?.description,
              furnitureItems: item?.furnitureItems,
              images: item?.images,
              moodboardImages: item?.moodboardImages,
            }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(false);

              const {
                style,
                images,
                midJourneyPrompt,
                furnitureItems,
                name,
                moodboardImages,
                description,
              } = values;
              let data = {
                style,
                images,
                midJourneyPrompt,
                furnitureItems,
                name,
                moodboardImages,
                description,
              };

              const uploadPromises = [];

              images.forEach((image, index) => {
                if (image.url !== "") {
                  const file = image.url;

                  if (file.size >= 1e7) {
                    return; 
                  }

                  const fd = new FormData();
                  fd.append("image", file);

                  const promise = axios
                    .post("/items/image-upload/", fd, {
                      headers: {
                        Authorization: `Bearer ${currentUserSessionStorage}`,
                      },
                    })
                    .then((resp) => {
                      const updatedImage = { ...image, url: resp.data.path }; 
                      const updatedImages = [...data.images];
                      updatedImages[index] = updatedImage;

                      data = {
                        ...data,
                        images: updatedImages,
                      };
                    })
                    .catch((error) => {
                      console.error("Image upload failed:", error);
                    });

                  uploadPromises.push(promise);
                }
              });

              moodboardImages.forEach((image, index) => {
                if (image.url !== "") {
                  const file = image.url;

                  if (file.size >= 1e7) {
                    return; 
                  }

                  const fd = new FormData();
                  fd.append("image", file);

                  const promise = axios
                    .post("/items/image-upload/", fd, {
                      headers: {
                        Authorization: `Bearer ${currentUserSessionStorage}`,
                      },
                    })
                    .then((resp) => {
                      const updatedImage = { ...image, url: resp.data.path }; 
                      const updatedImages = [...data.moodboardImages];
                      updatedImages[index] = updatedImage;

                      data = {
                        ...data,
                        moodboardImages: updatedImages,
                      };
                    })
                    .catch((error) => {
                      console.error("Image upload failed:", error);
                    });

                  uploadPromises.push(promise);
                }
              });

              furnitureItems.forEach((item, index) => {
                if (item.image !== "") {
                  const file = item.image;

                  if (file.size >= 1e7) {
                    return; 
                  }

                  const fd = new FormData();
                  fd.append("image", file);

                  const promise = axios
                    .post("/items/image-upload/", fd, {
                      headers: {
                        Authorization: `Bearer ${currentUserSessionStorage}`,
                      },
                    })
                    .then((resp) => {
                      const updatedItem = { ...item, image: resp.data.path }; 
                      const updatedItems = [...data.furnitureItems];
                      updatedItems[index] = updatedItem;

                      data = {
                        ...data,
                        furnitureItems: updatedItems,
                      };
                    })
                    .catch((error) => {
                      console.error("Image upload failed:", error);
                    });

                  uploadPromises.push(promise);
                }
              });

              Promise.all(uploadPromises)
                .then(() => {
                  updateVibe({ id: item._id, data: data });
                })
                .catch((error) => {
                  console.error("Image upload failed:", error);
                });
            }}
          >
            {({
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
              setValues,
            }) => (
              <Form noValidate autoComplete="off">
                <Typography
                  id="modal-modal-title"
                  fontWeight={"bold"}
                  fontSize={"20px"}
                  npmsvariants="h6"
                  mb={2}
                >
                  Update a Vibe
                </Typography>

                <Stack my={2}>
                  <TextField
                    id="outlined-basic"
                    label="Name"
                    variants="outlined"
                    name="name"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                    required
                    helperText={
                      getIn(touched, "name") && getIn(errors, "name")
                        ? getIn(errors, "name")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "name") && getIn(errors, "name")
                    )}
                  />
                </Stack>

                <Stack my={2}>
                  <TextField
                    id="outlined-basic"
                    label="Description"
                    variants="outlined"
                    name="description"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.description}
                    required
                    helperText={
                      getIn(touched, "description") &&
                      getIn(errors, "description")
                        ? getIn(errors, "description")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "description") &&
                        getIn(errors, "description")
                    )}
                  />
                </Stack>

                <Stack my={2}>
                  <TextField
                    id="outlined-basic"
                    label="Mid Journey Prompt"
                    variants="outlined"
                    name="midJourneyPrompt"
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.midJourneyPrompt}
                    required
                    helperText={
                      getIn(touched, "midJourneyPrompt") &&
                      getIn(errors, "midJourneyPrompt")
                        ? getIn(errors, "midJourneyPrompt")
                        : ""
                    }
                    error={Boolean(
                      getIn(touched, "midJourneyPrompt") &&
                        getIn(errors, "midJourneyPrompt")
                    )}
                  />
                </Stack>

                {FetchStyles && (
                  <Autocomplete
                    fullWidth
                    disablePortal
                    onChange={(event, value) =>
                      setValues({
                        ...values,
                        style: value?._id,
                      })
                    }
                    value={FetchStyles?.find(
                      (item) => item._id === values?.style
                    )}
                    name={`parentStyle`}
                    id="combo-box-demo"
                    options={FetchStyles}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        placeholder={"Select Style"}
                        name={`style`}
                        {...params}
                        onBlur={handleBlur}
                        helperText={
                          getIn(touched, "style") && getIn(errors, "style")
                            ? getIn(errors, "style")
                            : ""
                        }
                        error={Boolean(
                          getIn(touched, "style") && getIn(errors, "style")
                        )}
                      />
                    )}
                  />
                )}

                {values?.furnitureItems && (
                  <FieldArray name="furnitureItems">
                    {({ push, remove }) => (
                      <Box mt={2}>
                        {values?.furnitureItems?.map((p, index) => {
                          const name = `furnitureItems.[${index}].name`;
                          const touchedName = getIn(touched, name);
                          const errorName = getIn(errors, name);

                          const description = `furnitureItems.[${index}].description`;
                          const touchedDescription = getIn(
                            touched,
                            description
                          );
                          const errorDescription = getIn(errors, description);

                          const image = `furnitureItems.[${index}].image`;
                          const touchedImage = getIn(touched, image);
                          const errorImage = getIn(errors, image);

                          const url = `furnitureItems.[${index}].url`;
                          const touchedUrl = getIn(touched, url);
                          const errorUrl = getIn(errors, url);

                          const embedField = `furnitureItems.[${index}].embedField`;
                          const touchedEmbedField = getIn(touched, embedField);
                          const errorEmbedField = getIn(errors, embedField);

                          return (
                            <Box key={index}>
                              <Typography
                                variant="h6"
                                fontWeight={"bold"}
                                fontSize={"20px"}
                              >
                                Furniture {index + 1}
                              </Typography>
                              <Grid container spacing={2} mb={2}>
                                <Grid item xs={5}>
                                  <TextField
                                    variant="outlined"
                                    label="Name"
                                    name={name}
                                    value={p.name}
                                    required
                                    helperText={
                                      touchedName && errorName ? errorName : ""
                                    }
                                    error={Boolean(touchedName && errorName)}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                  />
                                </Grid>

                                <Grid item xs={5}>
                                  <TextField
                                    variant="outlined"
                                    label="url"
                                    name={url}
                                    fullWidth
                                    value={p.url}
                                    required
                                    helperText={
                                      touchedUrl && errorUrl ? errorUrl : ""
                                    }
                                    error={Boolean(touchedUrl && errorUrl)}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                  />
                                </Grid>

                                <Grid item xs={2}>
                                  <Button
                                    type="button"
                                    variant="contained"
                                    onClick={() => remove(index)}
                                  >
                                    x
                                  </Button>
                                </Grid>

                                <Grid item xs={4}>
                                  <Button variant="contained" component="label">
                                    Upload
                                    <input
                                      hidden
                                      accept="image/*"
                                      type="file"
                                      name="topViewImage"
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        const updatedItems =
                                          values.furnitureItems.map(
                                            (item, i) => {
                                              if (i === index) {
                                                return { ...item, image: file }; 
                                              }
                                              return item;
                                            }
                                          );
                                        setValues({
                                          ...values,
                                          furnitureItems: updatedItems,
                                        });
                                      }}
                                    />
                                  </Button>
                                </Grid>

                                <Grid item xs={6}>
                                  <Box>
                                    <img
                                      src={
                                        !(p.image instanceof File)
                                          ? p.image
                                            ? `${baseUrl}/g${p.image}`
                                            : "https://placehold.jp/90x90.png"
                                          : URL.createObjectURL(p.image)
                                      }
                                      alt={
                                        !(p.image instanceof File)
                                          ? p.image
                                            ? p.image.name
                                            : "placeHolder"
                                          : "placeHolder"
                                      }
                                      height={"100px"}
                                    />
                                  </Box>
                                  {touchedImage && errorImage && (
                                    <span className="error">
                                      {"Upload Image"}
                                    </span>
                                  )}
                                </Grid>

                                <Grid item xs={12}>
                                  <TextField
                                    variant="outlined"
                                    label="Description"
                                    name={description}
                                    fullWidth
                                    value={p.description}
                                    required
                                    helperText={
                                      touchedDescription && errorDescription
                                        ? errorDescription
                                        : ""
                                    }
                                    error={Boolean(
                                      touchedDescription && errorDescription
                                    )}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                  />
                                </Grid>

                                <Grid item xs={12}>
                                  <TextField
                                    placeholder="Write embed code"
                                    multiline
                                    rows={7}
                                    fullWidth
                                    name={embedField}
                                    value={p.embedField}
                                    required
                                    helperText={
                                      touchedEmbedField && errorEmbedField
                                        ? errorEmbedField
                                        : ""
                                    }
                                    error={Boolean(
                                      touchedEmbedField && errorEmbedField
                                    )}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                  />
                                </Grid>
                              </Grid>
                            </Box>
                          );
                        })}
                        <div
                          className={`${
                            values?.furnitureItems?.length < 1
                              ? "set-div"
                              : null
                          }`}
                        >
                          <Button
                            type="button"
                            variant="outlined"
                            onClick={() =>
                              push({
                                name: "",
                                description: "",
                                image: "",
                                url: "",
                                embedField: "",
                              })
                            }
                          >
                            Add Furniture
                          </Button>
                        </div>
                      </Box>
                    )}
                  </FieldArray>
                )}

                {values?.moodboardImages && (
                  <FieldArray name="moodboardImages">
                    {({ push, remove }) => (
                      <Box mt={2}>
                        {values?.moodboardImages?.map((p, index) => {
                          const url = `moodboardImages.[${index}].url`;
                          const touchedUrl = getIn(touched, url);
                          const errorUrl = getIn(errors, url);

                          return (
                            <Box key={index}>
                              <Typography
                                variant="h6"
                                fontWeight={"bold"}
                                fontSize={"20px"}
                              >
                                Mood Board Image {index + 1}
                              </Typography>

                              <Grid container spacing={2} mb={2}>
                                <Grid item xs={4}>
                                  <Button variant="contained" component="label">
                                    Upload
                                    <input
                                      hidden
                                      accept="image/*"
                                      type="file"
                                      name="topViewImage"
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        const updatedImages =
                                          values.moodboardImages.map(
                                            (image, i) => {
                                              if (i === index) {
                                                return { ...image, url: file }; 
                                              }
                                              return image;
                                            }
                                          );
                                        setValues({
                                          ...values,
                                          moodboardImages: updatedImages,
                                        });
                                      }}
                                    />
                                  </Button>
                                </Grid>

                                <Grid item xs={6}>
                                  <Box>
                                    <img
                                      src={
                                        !(p.url instanceof File)
                                          ? p.url
                                            ? `${baseUrl}/g${p.url}`
                                            : "https://placehold.jp/90x90.png"
                                          : URL.createObjectURL(p.url)
                                      }
                                      alt={
                                        !(p.url instanceof File)
                                          ? p.url
                                            ? p.url.name
                                            : "placeHolder"
                                          : "placeHolder"
                                      }
                                      height={"100px"}
                                    />
                                  </Box>
                                  {touchedUrl && errorUrl && (
                                    <span className="error">
                                      {"Upload Image"}
                                    </span>
                                  )}
                                </Grid>

                                <Grid item xs={2}>
                                  <Button
                                    type="button"
                                    variant="contained"
                                    onClick={() => remove(index)}
                                  >
                                    x
                                  </Button>
                                </Grid>
                              </Grid>
                            </Box>
                          );
                        })}
                        <div
                          className={`${
                            values?.moodboardImages?.length < 1
                              ? "set-div"
                              : null
                          }`}
                        >
                          <Button
                            type="button"
                            variant="outlined"
                            onClick={() =>
                              push({
                                url: "",
                              })
                            }
                          >
                            Add Mood Board Image
                          </Button>
                        </div>
                      </Box>
                    )}
                  </FieldArray>
                )}

                {values?.images && (
                  <FieldArray name="images">
                    {({ push, remove }) => (
                      <Box mt={2}>
                        {values?.images?.map((p, index) => {
                          const url = `images.[${index}].url`;
                          const touchedUrl = getIn(touched, url);
                          const errorUrl = getIn(errors, url);

                          return (
                            <Box key={index}>
                              <Typography
                                variant="h6"
                                fontWeight={"bold"}
                                fontSize={"20px"}
                              >
                                Image {index + 1}
                              </Typography>

                              <Grid container spacing={2} mb={2}>
                                <Grid item xs={4}>
                                  <Button variant="contained" component="label">
                                    Upload
                                    <input
                                      hidden
                                      accept="image/*"
                                      type="file"
                                      name="topViewImage"
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        const updatedImages = values.images.map(
                                          (image, i) => {
                                            if (i === index) {
                                              return { ...image, url: file }; 
                                            }
                                            return image;
                                          }
                                        );
                                        setValues({
                                          ...values,
                                          images: updatedImages,
                                        });
                                      }}
                                    />
                                  </Button>
                                </Grid>

                                <Grid item xs={6}>
                                  <Box>
                                    <img
                                      src={
                                        !(p.url instanceof File)
                                          ? p.url
                                            ? `${baseUrl}/g${p.url}`
                                            : "https://placehold.jp/90x90.png"
                                          : URL.createObjectURL(p.url)
                                      }
                                      alt={
                                        !(p.url instanceof File)
                                          ? p.url
                                            ? p.url.name
                                            : "placeHolder"
                                          : "placeHolder"
                                      }
                                      height={"100px"}
                                    />
                                  </Box>
                                  {touchedUrl && errorUrl && (
                                    <span className="error">
                                      {"Upload Image"}
                                    </span>
                                  )}
                                </Grid>

                                <Grid item xs={2}>
                                  <Button
                                    type="button"
                                    variant="contained"
                                    onClick={() => remove(index)}
                                  >
                                    x
                                  </Button>
                                </Grid>
                              </Grid>
                            </Box>
                          );
                        })}
                        <div
                          className={`${
                            values?.images?.length < 1 ? "set-div" : null
                          }`}
                        >
                          <Button
                            type="button"
                            variant="outlined"
                            onClick={() =>
                              push({
                                url: "",
                              })
                            }
                          >
                            Add Image
                          </Button>
                        </div>
                      </Box>
                    )}
                  </FieldArray>
                )}

                <Stack mt={2}>
                  <LoadingButton
                    loading={load}
                    variant="contained"
                    fullWidth
                    type="submit"
                  >
                    Save
                  </LoadingButton>
                </Stack>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
    </>
  );
}
